import { motion } from "framer-motion";
import { Warehouse, Crane, HardHat, Wrench } from "@phosphor-icons/react";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

const capabilities = [
  "High-precision cutting, welding, and finishing of structural steel",
  "Commercial and industrial warehousing construction",
  "Factory extensions and custom structural supports",
  "Structural steel for mining, industrial, and commercial facilities",
  "Demolition, dismantling, and replacement of damaged structures",
  "Design, fabrication, and on-site erection services",
  "Compliance with SABS and client-specific engineering specifications",
];

const projects = [
  {
    title: "Bulk24 Pty (Ltd) — Warehouse F",
    location: "Richards Bay, KwaZulu-Natal",
    size: "1,600 m\u00B2",
    scope: "Demolition / Dismantling, Design, Fabrication & Erection",
    description: "Large-scale structural replacement, integrating demolition of the existing structure, complete design, fabrication of new structural steel, and erection of a 1,600 m\u00B2 warehouse structure into a single, coordinated contract.",
    icon: <Warehouse size={24} weight="duotone" />,
    image: "/images/projects/warehouse-f.jpeg",
  },
  {
    title: "Sprint Logistics — Passage Warehouse Integration",
    location: "Richards Bay, KwaZulu-Natal",
    scope: "Design, Fabrication & Erection",
    description: "Constructed a new structure between two existing, operational buildings. Utilized existing adjacent columns as shared support columns for the new roof and frame. Required technical creativity and engineering adaptability in a confined operational environment.",
    icon: <Crane size={24} weight="duotone" />,
    image: "/images/projects/passage-warehouse.jpeg",
  },
  {
    title: "Bulk24 Pty (Ltd) — Warehouse D Repair",
    location: "Richards Bay, KwaZulu-Natal",
    scope: "Partial Demolition, Fabrication & Erection",
    description: "Rapid response to structural failure requiring partial demolition and dismantling of the damaged section, fabrication of new replacement structural components, and seamless integration with the remaining sound structure.",
    icon: <Wrench size={24} weight="duotone" />,
    image: "/images/projects/warehouse-d.jpeg",
  },
  {
    title: "Bulk24 Mobile Structure — Riverhorse Valley",
    location: "Riverhorse Valley, KwaZulu-Natal",
    size: "350 m\u00B2",
    scope: "Custom Design, Fabrication & Installation",
    description: "A custom-fabricated, fully mobile, and demountable steel structure designed as a warehouse extension for internal truck loading/offloading access. Solves a specific logistical problem with a non-permanent, relocatable solution.",
    icon: <HardHat size={24} weight="duotone" />,
    image: "/images/projects/riverhorse-mobile-warehouse.jpeg",
  },
];

export default function SteelFabrication() {
  return (
    <div>
      {/* HERO */}
      <section className="relative py-24 lg:py-32 overflow-hidden" data-testid="steel-hero">
        <div className="absolute inset-0">
          <img src="/images/projects/sugar-debagging-unit.jpeg" alt="KZNSC Sugar Debagging Unit" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0B2D2D]/85" />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#A7D129] mb-4">Services</motion.p>
            <motion.h1 variants={fadeUp} className="font-heading font-black text-5xl md:text-7xl tracking-tighter text-white leading-[0.9] mb-6">
              Steel<br />Fabrication
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-[#E0F5F2]/70 max-w-2xl leading-relaxed">
              KZNSC's Fabrication Division offers end-to-end services encompassing expert design, precision fabrication, and reliable on-site erection and installation. We specialize in structural steel for commercial, industrial, and mining facilities.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="py-20 lg:py-28 bg-white" data-testid="capabilities-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <motion.div className="lg:col-span-5" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#00A091] mb-3">What We Offer</p>
              <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-[#0B2D2D] mb-6">Fabrication Capabilities</h2>
              <p className="text-base text-[#0B2D2D]/70 leading-relaxed mb-6">
                Our fabrication division delivers structural steel solutions for commercial and industrial warehousing, factory extensions, and custom structural supports. All work meets SABS standards and client-specific engineering specifications.
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div className="img-zoom border border-[#0F4C5C]/20"><img src="/images/projects/warehouse-f.jpeg" alt="Warehouse F construction" className="w-full h-[120px] object-cover" /></div>
                <div className="img-zoom border border-[#0F4C5C]/20"><img src="/images/projects/passage-warehouse.jpeg" alt="Passage warehouse" className="w-full h-[120px] object-cover" /></div>
                <div className="img-zoom border border-[#0F4C5C]/20"><img src="/images/projects/warehouse-d.jpeg" alt="Warehouse D repair" className="w-full h-[120px] object-cover" /></div>
              </div>
            </motion.div>
            <motion.div className="lg:col-span-7" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <div className="bg-white border border-[#0F4C5C]/20 p-8">
                <div className="space-y-3">
                  {capabilities.map((cap, i) => (
                    <div key={i} className="flex items-start gap-3 py-2 border-b border-[#0F4C5C]/10 last:border-0" data-testid={`capability-${i}`}>
                      <div className="w-2 h-2 bg-[#A7D129] mt-1.5 flex-shrink-0" />
                      <span className="text-sm text-[#0B2D2D]/80 leading-relaxed">{cap}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PROJECT SPOTLIGHTS */}
      <section className="py-20 lg:py-28 bg-[#E0F5F2]" data-testid="steel-projects">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
          <motion.div className="mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#00A091] mb-3">Warehousing Construction</p>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-[#0B2D2D]">Project Spotlights</h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-8" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            {projects.map((proj, i) => (
              <motion.div key={i} variants={fadeUp} className="card-clean rounded-sm overflow-hidden" data-testid={`steel-project-${i}`}>
                <div className="img-zoom h-[200px]">
                  <img src={proj.image} alt={proj.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#00A091] text-white flex items-center justify-center">{proj.icon}</div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-[#0B2D2D] leading-tight">{proj.title}</h3>
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#00A091]">{proj.location}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {proj.size && (
                    <span className="bg-[#A7D129] text-[#0B2D2D] font-mono text-[9px] uppercase tracking-[0.15em] font-bold px-3 py-1">{proj.size}</span>
                  )}
                  <span className="bg-[#0F4C5C] text-white font-mono text-[9px] uppercase tracking-[0.15em] font-bold px-3 py-1">{proj.scope}</span>
                </div>

                <p className="text-sm text-[#0B2D2D]/70 leading-relaxed">{proj.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0B2D2D] py-16 relative noise-overlay" data-testid="steel-cta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading font-bold text-2xl md:text-4xl tracking-tight text-white mb-4">Need Custom Steel Fabrication?</h2>
            <p className="text-[#E0F5F2]/60 text-sm max-w-xl mx-auto mb-6">From warehouse construction to structural repairs, our fabrication division delivers precision-engineered solutions.</p>
            <a href="/contact" className="inline-flex bg-[#A7D129] text-[#0B2D2D] font-bold uppercase tracking-widest text-xs px-8 py-4 hover:bg-[#00A091] hover:text-white transition-colors" data-testid="steel-cta-btn">
              Request A Quote
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
